// Page design: the elements of the layout a page is drawn from (a layout kept as data), over the page as
// boxes to move and size, and beside it their values. A change is one value of the layout
// (layout/<layout>/<element>/<prop>), kept by the output's layout editor until it is previewed and
// applied, and it changes every page drawn from that layout. What may change on each kind of element,
// and within what, is the layout's own rule (its `kinds`, page size and adapter ranges/steps);
// the page shows its last render until the preview draws it anew.
import { mountPagePictures } from './output-page-pictures.mjs';
import { editableElements, elementAddress } from './output-page-elements.mjs';

const SVG = 'http://www.w3.org/2000/svg';
const PROP_LABELS = Object.freeze({ x: '가로 위치', y: '세로 위치', w: '폭', h: '높이', height: '높이', s: '크기', bottom: '아래 끝', size: '글자 크기', font: '글꼴', color: '색' });
const inside = prop => prop.startsWith('style.');

function pageDimensions(size) {
  const [x, y] = Array.isArray(size) ? size : [size?.width, size?.height];
  return Number.isFinite(x) && x > 0 && Number.isFinite(y) && y > 0 ? { x, y } : null;
}

function node(tag, attributes = {}, children = []) {
  const element = document.createElement(tag);
  for (const [name, value] of Object.entries(attributes)) {
    if (name === 'text') element.textContent = value;
    else element.setAttribute(name, value);
  }
  element.append(...children);
  return element;
}
const addressOf = (element, prop) => `layout/${element.layout}/${element.id}/${prop}`;
const range = (rule, { pageSize, ranges = {} } = {}) => ranges[rule] ?? [rule === 'size' ? 1 : 0, pageDimensions(pageSize)?.[rule] ?? Infinity];
const stepOf = (rule, { steps = {} } = {}) => steps[rule] ?? (rule === 'size' ? 0.5 : 1);
const minimum = (kind, prop) => ['text', 'art', 'shape', 'image'].includes(kind) && ['w', 'h', 'height'].includes(prop) ? 1 : -Infinity;
const clamp = (rule, value, lower = 0, settings = {}) => {
  const [min, max] = range(rule, settings), step = stepOf(rule, settings);
  return Math.min(max, Math.max(min, lower, Number((Math.round(value / step) * step).toPrecision(12))));
};

/** An element's box on the page with its values changed from the ones it was drawn with; a square one (s) grows both ways. */
export function movedBox(element, values) {
  const d = prop => (prop in values && prop in element ? values[prop] - element[prop] : 0);
  const [x, y, w, h] = element.box;
  return [x + d('x'), y + d('y'), w + d('w') + d('s'), h + d('h') + d('height') + d('s') + (('bottom' in element) ? d('bottom') - d('y') : 0)];
}

/** The values a move by (dx, dy) or a resize by (dw, dh) in points gives an element, within its rules. */
export function draggedValues(element, rules, values, { dx = 0, dy = 0, dw = 0, dh = 0 }, settings = {}) {
  const next = { ...values };
  const set = (prop, delta) => { if (prop in rules && prop in next && delta) next[prop] = clamp(rules[prop], next[prop] + delta, minimum(element.kind, prop), settings); };
  set('x', dx); set('y', dy);
  // A group is placed by its top and its bottom: moved, both go; resized, the bottom does.
  set('bottom', dy + dh);
  set('w', dw); set('h', dh); set('height', dh);
  // A square is sized by the larger of the two.
  set('s', Math.abs(dw) >= Math.abs(dh) ? dw : dh);
  return next;
}

/**
 * editor: { available(), list(), save({ address, base, value }), revert(address) } (the output's layoutEdits);
 * onEdited(pending) hears the pending layout edits after a save or an undo, onPreview() asks for a preview.
 */
export function mountPageDesign({ onEdited = () => {}, onPreview = () => {} } = {}) {
  const heading = node('h3', { text: '페이지 디자인' });
  const scope = node('p', { class: 'es-page-design-scope', 'data-testid': 'studio-page-design-scope' });
  const note = node('p', { class: 'es-page-design-note' });
  const list = node('ol', { class: 'es-page-design-list', 'data-testid': 'studio-page-design-list' });
  const form = node('div', { class: 'es-page-design-form', 'data-testid': 'studio-page-design-form' });
  const additions = node('div', { class: 'es-page-design-additions', 'data-testid': 'studio-page-design-additions' });
  const removed = node('div', { class: 'es-page-design-removed', 'data-testid': 'studio-page-design-removed' });
  const controls = node('fieldset', { class: 'es-page-design-controls', 'aria-label': '페이지 요소 편집' }, [additions, list, form, removed]);
  const status = node('p', { class: 'es-page-design-status', 'aria-live': 'polite', 'data-testid': 'studio-page-design-status' });
  const preview = node('button', { type: 'button', class: 'es-page-design-preview', 'data-testid': 'studio-page-design-preview', text: '이 디자인으로 미리보기', hidden: '' });
  // The page's pictures, swapped where each is chosen: their swaps wait with the layout edits.
  const pictures = mountPagePictures({
    onEdited: known => { pending = known; draw(); onEdited(known); },
    onLight: (boxes, on) => lightPicture(boxes, on),
  });
  const element = node('aside', { class: 'es-page-design', hidden: '', 'aria-label': '페이지 디자인', 'data-testid': 'studio-page-design' },
    [heading, scope, note, controls, status, preview, pictures.element]);
  let overlay = null, figure = null, elements = [], layouts = null, editor = null, pending = [], choices = {}, selected = null, ticket = 0, drag = null;
  let layoutId = null, definitions = null, rendered = [], saving = false;
  let pageSize = null, design = {}, settings = {};
  const saveWaiters = [];

  const rulesOf = item => layouts?.kinds?.[item.kind] ?? {};
  const labelOf = (kind, prop) => design.kindLabels?.[kind]?.[prop] ?? design.propLabels?.[prop] ?? PROP_LABELS[prop] ?? prop;
  /** The element's values now: as drawn, with its pending edits. */
  function valuesOf(item) {
    if (item.currentValues) return item.currentValues;
    const values = {};
    for (const prop of Object.keys(rulesOf(item))) {
      if (!(prop in item)) continue;
      const edit = pending.find(entry => entry.address === addressOf(item, prop));
      values[prop] = edit ? edit.value : item[prop];
    }
    return values;
  }
  const editedProps = item => Object.keys(valuesOf(item)).filter(prop => pending.some(entry => entry.address === addressOf(item, prop)));
  const structureEdit = item => pending.find(entry => entry.address === elementAddress(layoutId, item.id));
  const isEdited = item => Boolean(structureEdit(item) || editedProps(item).length);

  function setSaving(value) {
    saving = value;
    controls.disabled = value;
    overlay?.setAttribute('aria-disabled', String(value));
    if (value) status.textContent = '저장하는 중… 완료되면 다시 편집할 수 있습니다.';
    else for (const resolve of saveWaiters.splice(0)) resolve();
  }
  // One drag can write several properties. Leaving or capturing the canvas waits
  // for the entire action, including properties not submitted to the adapter yet.
  function flush() { return saving ? new Promise(resolve => saveWaiters.push(resolve)) : Promise.resolve(); }

  async function saveElement(base, value) {
    if (saving) return;
    const savedLayout = layoutId;
    const wasPublished = definitions.some(item => item.id === base?.id);
    setSaving(true);
    try {
      pending = await editor.save({ address: elementAddress(savedLayout, (value ?? base).id), base, value });
      if (layoutId === savedLayout) selected = value?.id ?? null;
      status.textContent = design.immediate ? value ? '저장했습니다 · 페이지에 바로 반영했습니다' : '삭제했습니다 · 페이지에 바로 반영했습니다'
        : value ? '저장했습니다 · 미리보기로 실제 모습을 봅니다'
        : `${wasPublished ? '삭제했습니다 · 아래에서 복원할 수 있습니다' : '추가한 요소를 취소했습니다'} · 미리보기를 다시 만들면 쪽에 반영됩니다`;
      onEdited(pending); pictures.update(pending);
      return true;
    } catch (error) { status.textContent = error.message; return false; }
    finally { setSaving(false); draw(); }
  }

  function newElement(kind) {
    const rules = layouts.kinds[kind];
    const value = { name: kind === 'text' ? '추가한 글' : '추가한 그림', x: pageSize.x * 0.1, y: pageSize.y * 0.1,
      w: pageSize.x * (kind === 'text' ? 0.8 : 0.2),
      ...(kind === 'text' ? { height: pageSize.y * 0.1, size: Math.min(pageSize.x, pageSize.y) * 0.03 } : {}) };
    for (const prop of ['font', 'color']) if (Array.isArray(rules[prop])) value[prop] = rules[prop][0];
    Object.assign(value, design.defaults?.[kind]);
    for (const [prop, rule] of Object.entries(rules)) {
      if (typeof value[prop] === 'number' && !Array.isArray(rule)) value[prop] = clamp(rule, value[prop], minimum(kind, prop), settings);
    }
    return { ...value, id: `${kind}-${crypto.randomUUID()}`, kind };
  }

  function addControls() {
    additions.replaceChildren();
    additions.hidden = !editor || !definitions || !pageSize;
    if (additions.hidden) return;
    const textButton = node('button', { type: 'button', text: '글 상자 추가', 'data-testid': 'studio-page-design-add-text' });
    const artButton = node('button', { type: 'button', text: '그림 추가', 'data-testid': 'studio-page-design-add-art' });
    const entry = node('div', { class: 'es-page-design-entry' });
    textButton.addEventListener('click', () => {
      const input = node('textarea', { rows: '3', maxlength: '4000', 'aria-label': '추가할 글', 'data-testid': 'studio-page-design-new-text' });
      const submit = node('button', { type: 'button', text: '이 글 추가', 'data-testid': 'studio-page-design-create-text' });
      submit.addEventListener('click', async () => {
        if (!input.value.trim()) { status.textContent = '추가할 글을 입력해 주세요.'; input.focus(); return; }
        submit.disabled = true;
        const ok = await saveElement(null, { ...newElement('text'), text: input.value });
        if (ok) entry.replaceChildren(); else submit.disabled = false;
      });
      entry.replaceChildren(input, submit); input.focus();
    });
    artButton.addEventListener('click', async () => {
      const mine = ticket;
      artButton.disabled = true;
      try {
        const library = await editor.pictures();
        if (mine !== ticket) return;
        const allowed = library.kinds ? new Set(Object.values(library.kinds).flat()) : null;
        const gallery = node('div', { class: 'es-page-pictures-grid', 'data-testid': 'studio-page-design-art-library' });
        for (const picture of library.pictures.filter(item => !allowed || allowed.has(item.file))) {
          const button = node('button', { type: 'button', class: 'es-page-pictures-option', title: picture.file, 'aria-label': picture.file,
            'data-testid': 'studio-page-design-create-art', 'data-value': picture.file });
          button.append(node('img', { src: editor.pictureUrl(picture.file), alt: '', loading: 'lazy', width: '52', height: '52' }));
          button.addEventListener('click', async () => {
            const value = newElement('art');
            value.h = clamp('y', value.h ?? value.w * picture.h / picture.w, 1, settings);
            const ok = await saveElement(null, { ...value, image: picture.file });
            if (ok) entry.replaceChildren();
          });
          gallery.append(button);
        }
        entry.replaceChildren(gallery);
      } catch (error) { status.textContent = error.message; }
      finally { artButton.disabled = false; }
    });
    if (layouts.kinds.text) additions.append(textButton);
    if (layouts.kinds.art && typeof editor.pictures === 'function' && typeof editor.pictureUrl === 'function') additions.append(artButton);
    additions.append(entry);
  }

  async function save(item, next) {
    if (saving) return;
    const values = valuesOf(item);
    const changed = Object.keys(next).filter(prop => next[prop] !== values[prop]);
    if (!changed.length) { draw(); return; }
    const savedEditor = editor;
    setSaving(true);
    try {
      for (const prop of changed) pending = await savedEditor.save({ address: addressOf(item, prop), base: values[prop], value: next[prop] });
      status.textContent = `저장했습니다 · ${changed.map(prop => labelOf(item.kind, prop)).join('·')} — ${design.immediate ? '페이지에 바로 반영했습니다' : '미리보기로 실제 모습을 봅니다'}`;
      onEdited(pending); pictures.update(pending);
    } catch (error) { status.textContent = error.message; }
    finally { setSaving(false); }
    draw();
  }
  async function revertAddresses(addresses, message) {
    if (saving) return;
    const savedEditor = editor;
    setSaving(true);
    try {
      for (const address of addresses) pending = await savedEditor.revert(address);
      status.textContent = `${message} · 미리보기를 다시 만들면 쪽에 반영됩니다`;
      onEdited(pending); pictures.update(pending);
    } catch (error) { status.textContent = error.message; }
    finally { setSaving(false); draw(); }
  }
  function revert(item) {
    const addresses = editedProps(item).map(prop => addressOf(item, prop));
    const structural = structureEdit(item);
    if (structural) addresses.unshift(structural.address);
    return revertAddresses(addresses, `${item.name}을(를) 원래대로 되돌렸습니다`);
  }

  // Chosen from the list, the element's box comes into view on the page, and the arrows move it.
  function select(id) {
    selected = id; draw();
    overlay?.querySelector(`[data-element="${id}"] .es-design-box`)?.scrollIntoView({ block: 'nearest' });
    overlay?.focus({ preventScroll: true });
  }

  /** Lights the boxes a picture is drawn in, over the page, while its row is pointed at. */
  function lightPicture(boxes, on) {
    overlay?.querySelectorAll('.es-design-picture-light').forEach(rect => rect.remove());
    if (!on || !overlay) return;
    for (const [x, y, w, h] of boxes) {
      const rect = document.createElementNS(SVG, 'rect');
      for (const [name, value] of Object.entries({ x, y, width: w, height: h })) rect.setAttribute(name, String(value));
      rect.setAttribute('class', 'es-design-picture-light');
      overlay.append(rect);
    }
  }
  // An element's name sits just above its box, off what the page prints (inside it, at the page's top edge);
  // it shows for the element chosen and the one pointed at.
  function placeLabel(label, x, y) {
    label.setAttribute('x', String(x + 2));
    label.setAttribute('y', String(y >= 16 ? y - 4 : y + 13));
  }
  function drawOverlay() {
    overlay?.remove();
    overlay = document.createElementNS(SVG, 'svg');
    overlay.setAttribute('class', 'es-page-design-overlay');
    overlay.setAttribute('viewBox', `0 0 ${pageSize.x} ${pageSize.y}`);
    overlay.setAttribute('preserveAspectRatio', 'none');
    overlay.setAttribute('tabindex', '0');
    overlay.setAttribute('aria-disabled', String(saving));
    overlay.setAttribute('aria-label', '페이지 요소 · 끌어 옮기고 모서리로 크기를 바꿉니다 · 화살표로 옮깁니다(Shift 10배)');
    overlay.dataset.testid = 'studio-page-design-overlay';
    for (const item of elements) {
      const [x, y, w, h] = movedBox(item, valuesOf(item));
      const group = document.createElementNS(SVG, 'g');
      group.dataset.element = item.id;
      group.setAttribute('class', ['es-design-element', item.id === selected && 'es-design-element--selected', isEdited(item) && 'es-design-element--edited'].filter(Boolean).join(' '));
      const box = document.createElementNS(SVG, 'rect');
      for (const [name, value] of Object.entries({ x, y, width: Math.max(1, w), height: Math.max(1, h) })) box.setAttribute(name, String(value));
      box.setAttribute('class', 'es-design-box');
      box.dataset.testid = `studio-page-design-box-${item.id}`;
      const title = document.createElementNS(SVG, 'title'); title.textContent = item.name; box.append(title);
      const label = document.createElementNS(SVG, 'text');
      label.setAttribute('class', 'es-design-label'); label.textContent = item.name;
      placeLabel(label, x, y);
      group.append(box, label);
      const rules = rulesOf(item);
      // Only an element whose size may change has a handle to size it by.
      if (['w', 'h', 'height', 's', 'bottom'].some(prop => prop in rules)) {
        const handle = document.createElementNS(SVG, 'rect');
        for (const [name, value] of Object.entries({ x: x + w - 7, y: y + h - 7, width: 14, height: 14 })) handle.setAttribute(name, String(value));
        handle.setAttribute('class', 'es-design-handle');
        handle.dataset.testid = `studio-page-design-handle-${item.id}`;
        handle.addEventListener('pointerdown', event => start(event, item, 'size'));
        group.append(handle);
      }
      box.addEventListener('pointerdown', event => start(event, item, 'move'));
      overlay.append(group);
    }
    overlay.addEventListener('keydown', event => {
      const item = editor && elements.find(entry => entry.id === selected);
      const factor = event.shiftKey ? 10 : 1, x = stepOf('x', settings) * factor, y = stepOf('y', settings) * factor;
      const delta = { ArrowLeft: { dx: -x }, ArrowRight: { dx: x }, ArrowUp: { dy: -y }, ArrowDown: { dy: y } }[event.key];
      if (!item || !delta) return;
      event.preventDefault(); event.stopPropagation();
      if (saving) return;
      void save(item, draggedValues(item, rulesOf(item), valuesOf(item), delta, settings));
    });
    figure.querySelector('.es-output-reader-frame').append(overlay);
  }

  // A drag in page points: the overlay is the page, scaled to the figure.
  function start(event, item, mode) {
    event.preventDefault(); event.stopPropagation();
    if (saving) return;
    if (selected !== item.id) { selected = item.id; draw(); }
    // Without the local workshop the layout is shown, not changed.
    if (!editor) return;
    const bounds = overlay.getBoundingClientRect();
    drag = { item, mode, x: event.clientX, y: event.clientY, scaleX: pageSize.x / bounds.width, scaleY: pageSize.y / bounds.height, values: valuesOf(item) };
    overlay.setPointerCapture?.(event.pointerId);
    overlay.classList.add('es-page-design-overlay--dragging');
  }
  function deltaOf(event) {
    const dx = (event.clientX - drag.x) * drag.scaleX, dy = (event.clientY - drag.y) * drag.scaleY;
    return drag.mode === 'move' ? { dx, dy } : { dw: dx, dh: dy };
  }
  function onMove(event) {
    if (!drag) return;
    const next = draggedValues(drag.item, rulesOf(drag.item), drag.values, deltaOf(event), settings);
    const [x, y, w, h] = movedBox(drag.item, next);
    const group = overlay.querySelector(`[data-element="${drag.item.id}"]`);
    const box = group.querySelector('.es-design-box');
    box.setAttribute('x', String(x)); box.setAttribute('y', String(y)); box.setAttribute('width', String(Math.max(1, w))); box.setAttribute('height', String(Math.max(1, h)));
    placeLabel(group.querySelector('.es-design-label'), x, y);
    const handle = group.querySelector('.es-design-handle');
    if (handle) { handle.setAttribute('x', String(x + w - 7)); handle.setAttribute('y', String(y + h - 7)); }
  }
  function onUp(event) {
    if (!drag) return;
    const { item, values } = drag;
    const next = draggedValues(item, rulesOf(item), values, deltaOf(event), settings);
    drag = null;
    overlay?.classList.remove('es-page-design-overlay--dragging');
    void save(item, next);
  }
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);

  function field(item, prop, rule, value) {
    const label = labelOf(item.kind, prop);
    const [min, max] = range(rule, settings);
    const control = Array.isArray(rule)
      ? node('select', { 'data-prop': prop, 'data-testid': `studio-page-design-${prop}` }, rule.map(option => node('option', { value: option, text: choices[prop]?.[option] ?? option })))
      : node('input', { type: 'number', step: String(stepOf(rule, settings)), min: String(Math.max(min, minimum(item.kind, prop))), ...(Number.isFinite(max) ? { max: String(max) } : {}), 'data-prop': prop, 'data-testid': `studio-page-design-${prop}` });
    control.value = String(value);
    if (!editor) control.disabled = true;
    control.addEventListener('change', () => {
      const next = { ...valuesOf(item) };
      next[prop] = Array.isArray(rule) ? control.value : clamp(rule, Number(control.value), minimum(item.kind, prop), settings);
      void save(item, next);
    });
    return node('label', { class: 'es-page-design-field' }, [node('span', { text: label }), control]);
  }

  function drawPanel() {
    list.replaceChildren(...elements.map(item => {
      const row = node('button', { type: 'button', class: 'es-page-design-item', 'data-element': item.id, 'aria-pressed': String(item.id === selected), 'data-testid': 'studio-page-design-item' },
        [node('span', { text: item.name }), ...(isEdited(item) ? [node('small', { class: 'es-page-design-edited', text: '고침' })] : [])]);
      row.addEventListener('click', () => select(item.id));
      return row;
    }));
    const item = elements.find(entry => entry.id === selected);
    if (!item) { form.replaceChildren(node('p', { class: 'es-page-design-hint', text: '요소를 눌러 고릅니다.' })); return; }
    const values = valuesOf(item), rules = rulesOf(item);
    const undo = node('button', { type: 'button', class: 'es-page-design-undo', 'data-testid': 'studio-page-design-revert', text: '원래대로', ...(isEdited(item) ? {} : { hidden: '' }) });
    undo.addEventListener('click', () => { void revert(item); });
    // Its place and size first, then its inside (a card's icon, title and text), under their own heading.
    const props = Object.keys(values);
    const insideProps = props.filter(inside);
    form.replaceChildren(node('strong', { text: item.name }),
      ...props.filter(prop => !inside(prop)).map(prop => field(item, prop, rules[prop], values[prop])),
      ...(insideProps.length ? [node('span', { class: 'es-page-design-subhead', 'data-testid': 'studio-page-design-inside', text: '안쪽' }),
        ...insideProps.map(prop => field(item, prop, rules[prop], values[prop]))] : []),
      undo);
    if (editor && item.definition) {
      if (item.kind === 'text' && (design.canEditText?.(item.definition) ?? true)) {
        const content = node('textarea', { rows: '3', maxlength: '4000', 'aria-label': '상자의 글', 'data-testid': 'studio-page-design-text' });
        content.value = item.definition.text;
        content.addEventListener('change', () => { void saveElement(item.definition, { ...item.definition, text: content.value }); });
        form.append(node('label', { class: 'es-page-design-content' }, [node('span', { text: '상자의 글' }), content]));
      }
      if (design.canDeleteElement?.(item.definition) ?? ['text', 'art'].includes(item.kind)) {
        const remove = node('button', { type: 'button', text: '이 요소 삭제', 'data-testid': 'studio-page-design-delete' });
        remove.addEventListener('click', () => { void saveElement(item.definition, null); });
        form.append(remove);
      }
    }
  }
  function draw() {
    if (!figure || !pageSize) return;
    if (definitions) elements = editableElements(definitions, rendered, layoutId, layouts.kinds, pending);
    removed.replaceChildren();
    for (const edit of pending.filter(entry => entry.address.startsWith(`layout/${layoutId}/`) && entry.address.endsWith('/element') && entry.value === null)) {
      const restore = node('button', { type: 'button', text: `${edit.base.name} 복원`, 'data-testid': 'studio-page-design-restore' });
      restore.addEventListener('click', () => { void revertAddresses([edit.address], '삭제한 요소를 복원했습니다'); });
      removed.append(restore);
    }
    drawOverlay();
    drawPanel();
    preview.hidden = !editor || !pending.length;
  }

  /**
   * Shows the layout elements of `page` over `figure` (found: partsOf(page)): editable with `editor` when
   * it answers, `pending` its layout edits, `choices` the names of a choice's values.
   * pageSize ([width, height] or { width, height }) overrides layouts.page, then the measured found.size.
   * design supplies the adapter's defaults by kind, numeric ranges/steps by rule, propLabels/kindLabels,
   * heading, scope (text or a function of { page, layout, found }), notes (including picturesNote), canEditText(definition),
   * canDeleteElement(definition) (defaults to text/art only), and immediate for edits applied directly to the page.
   */
  async function show(page, pageFigure, found, { editor: layoutEditor = null, pending: known = [], choices: named = {}, pageSize: suppliedSize, design: suppliedDesign = {} } = {}) {
    const mine = ++ticket;
    drag = null;
    element.hidden = false;
    figure = pageFigure;
    elements = found?.elements ?? []; rendered = elements; layouts = found?.layouts ?? null; editor = layoutEditor; pending = known; choices = named;
    pageSize = pageDimensions(suppliedSize ?? layouts?.page ?? found?.size);
    design = suppliedDesign;
    settings = { pageSize: pageSize ? [pageSize.x, pageSize.y] : null, ranges: design.ranges, steps: design.steps };
    layoutId = found?.layoutId ?? null;
    definitions = layouts?.layouts?.[layoutId]?.elements ?? null;
    addControls();
    if (!elements.some(item => item.id === selected)) selected = null;
    const layout = layouts?.layouts?.[layoutId];
    const title = design.heading ?? '페이지 디자인';
    heading.textContent = layout ? `${title} · ${layout.title}` : title;
    element.setAttribute('aria-label', title);
    if (editor && ['pictures', 'pictureUrl', 'pictureLabel', 'meaningName'].every(method => typeof editor[method] === 'function')) {
      void pictures.show(found?.parts ?? [], { editor, pending, note: design.picturesNote });
    } else pictures.clear();
    if (!layout || !pageSize) {
      overlay?.remove(); overlay = null; figure = null;
      removed.replaceChildren();
      scope.textContent = '';
      note.textContent = design.noLayoutNote ?? '이 페이지에는 편집할 디자인 정보가 없습니다.';
      list.replaceChildren(); form.replaceChildren(); preview.hidden = true; status.textContent = '';
      return;
    }
    scope.textContent = typeof design.scope === 'function' ? design.scope({ page, layout, found }) : design.scope ?? '';
    note.textContent = editor ? design.editableNote ?? '상자를 끌어 옮기고 모서리로 크기를 바꿉니다. 미리보기로 저장한 디자인을 확인합니다.'
      : design.readOnlyNote ?? '현재 페이지 디자인을 보고 있습니다. 편집기를 연결하면 고칠 수 있습니다.';
    if (mine !== ticket) return;
    draw();
    if (!editor) overlay.classList.add('es-page-design-overlay--read-only');
  }
  preview.addEventListener('click', async () => {
    preview.disabled = true;
    status.textContent = '미리보기를 만드는 중… 페이지를 새로 그립니다';
    try {
      await onPreview();
      status.textContent = '미리보기를 만들었습니다 · 쪽이 새 디자인으로 그려졌습니다';
    } catch (error) { status.textContent = `미리보기를 만들지 못했습니다 — ${error.message}`; }
    finally { preview.disabled = false; }
  });
  function clear() { ticket += 1; drag = null; overlay?.remove(); overlay = null; figure = null; element.hidden = true; list.replaceChildren(); form.replaceChildren(); additions.replaceChildren(); removed.replaceChildren(); pictures.clear(); }
  function dispose() { clear(); window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); }
  return { element, show, clear, dispose, flush };
}
